/**
 * Ordering utilities for maintaining list item order
 * 
 * Architecture: Domain layer utility
 * Uses gap-based ordering (e.g., 0, 100, 200) to allow efficient reordering
 */

import type { Item } from '../domain/models'

/**
 * Standard gap between item orders
 * Allows for 99 items to be inserted between any two existing items
 */
export const ORDER_GAP = 100

/**
 * Calculate the next order value for appending a new item to a list
 * @param items - Current items in the list (dictionary or array)
 * @returns The next order value (maxOrder + ORDER_GAP)
 */
export function getNextOrder(items: Record<string, Item> | Item[] | undefined): number {
  if (!items) {
    return ORDER_GAP
  }

  const itemsArray = Array.isArray(items) ? items : Object.values(items)
  if (itemsArray.length === 0) {
    return ORDER_GAP
  }

  const maxOrder = itemsArray.reduce((max, item) => {
    const order = typeof item.order === 'number' ? item.order : 0
    return order > max ? order : max
  }, 0)

  return maxOrder + ORDER_GAP
}

/**
 * Calculate order values for multiple items being added at once
 * @param existingItems - Current items in the list (dictionary or array)
 * @param count - Number of new items to add
 * @returns Array of order values
 */
export function getNextOrders(existingItems: Record<string, Item> | Item[] | undefined, count: number): number[] {
  const startOrder = getNextOrder(existingItems)
  return Array.from({ length: count }, (_, i) => startOrder + (i * ORDER_GAP))
}
